import React, {
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { Link, useParams } from "react-router-dom";
import { formatInTimeZone } from "date-fns-tz";
import { toast } from "react-toastify";
import {
  DataTableColumn,
  EnhancedTable,
  Grid,
  IconButton,
  Icons,
  Text,
  formatRange,
  formatTimeRange,
  getDateRangeByValue,
  useClientTable,
} from "@pushpress/shared-components";
import { DateRangeValue } from "@pushpress/shared-components/build/components/selectors/DateRangeSelector/DateRangeSelector.types";
import Dashboard from "../../../layouts/Dashboard";
import { defaultToastOptions, extractError } from "../../../utils/misc";
import { AppContext } from "../../../context/app";
import useUrlSearchParams from "../../../hooks/useUrlSearchParams";
import useDownload from "../../../hooks/useDownload";
import { CalendarProvider } from "../CalendarProvider/CalendarProvider";
import {
  AttendanceReportFragment,
  useGetAttendanceReportQuery,
  useGetFilterSubscriptionsQuery,
  useGetProfileQuery,
} from "./Attendance-generated.hooks";
import AttendanceHeader from "./AttendanceHeader";
import useToggle from "../../../hooks/state";
import AttendanceStatus from "./AttendanceStatus";
import {
  createAttendanceReportCsv,
  filterAttendanceReport,
  getAttendanceReportFilterTagsMap,
} from "./AttendanceReport.utils";
import AttendanceFilter, { AttendanceFilterValue } from "./AttendanceFilter";
import AttendanceMenu from "./AttendanceMenu";

const AttendanceReport = () => {
  const { userUuid }: { userUuid: string } = useParams();
  const { subscriptionUuid } = useUrlSearchParams<{
    subscriptionUuid?: string;
  }>();

  return (
    <Dashboard>
      <CalendarProvider>
        <AttendanceReportContent
          userUuid={userUuid}
          subscriptionUuid={subscriptionUuid}
        />
      </CalendarProvider>
    </Dashboard>
  );
};

interface AttendanceReportContentProps {
  userUuid: string;
  subscriptionUuid?: string;
}

export const AttendanceReportContent = (
  props: AttendanceReportContentProps,
) => {
  const { userUuid, subscriptionUuid } = props;

  // hooks

  const { client } = useContext(AppContext);

  // states

  const [dateRange, setDateRange] = useState<DateRangeValue>("last-90-days");
  const [subscriptionStarted, subscriptionStartedActions] = useToggle();

  // api

  const { data: profile } = useGetProfileQuery({
    variables: {
      input: {
        clientUuid: client.uuid,
        userUuid,
        clientUserUuid: undefined,
        linkedAccounts: undefined,
      },
    },
    skip: !userUuid,
  });

  const range = getDateRangeByValue(dateRange, "UTC");

  const {
    data: attendanceReport,
    loading,
    refetch,
  } = useGetAttendanceReportQuery({
    variables: {
      input: {
        userUuid,
        startDate: range.start,
        endDate: range.end,
      },
    },
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
    onError(err: unknown) {
      toast.error(extractError(err), defaultToastOptions);
    },
  });

  const { data: { getSubscriptions } = {} } = useGetFilterSubscriptionsQuery({
    variables: {
      getSubscriptionInput: {
        clientUuid: client.uuid,
        userUuid,
        calendarItemUuid: null,
        limit: null,
        page: null,
      },
    },
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
    onError(err: unknown) {
      toast.error(extractError(err), defaultToastOptions);
    },
  });
  const subscriptions = getSubscriptions?.subscriptions;

  // table

  const columns = useMemo<DataTableColumn<AttendanceReportFragment, string>[]>(
    () => [
      {
        header: "Name",
        key: "name",
        isSortable: true,
        showUp: "md",
        sort: (a: AttendanceReportFragment, b: AttendanceReportFragment) => {
          return a.calendarItem?.title.localeCompare(b.calendarItem?.title);
        },
        cell: (row: AttendanceReportFragment) => (
          <>
            {row.calendarItem ? (
              <div>
                <Text
                  component={Link}
                  to={`/schedule/${row.calendarItem?.uuid}`}
                  variant="body-lg"
                  color="pacific-primary"
                >
                  {row.calendarItem?.title}
                </Text>
                <Text component="div" variant="body-lg" color="wolf-600">
                  {row.calendarItem?.calendarItemType.name}
                </Text>
              </div>
            ) : (
              <div>
                <Text variant="body-lg" color="wolf-800">
                  Open Gym
                </Text>
                <Text component="div" variant="body-lg" color="wolf-600">
                  --
                </Text>
              </div>
            )}
          </>
        ),
      },
      {
        header: "Date",
        key: "date",
        isSortable: true,
        sort: (a: AttendanceReportFragment, b: AttendanceReportFragment) => {
          const dateA =
            a.calendarItem?.startDatetime ||
            new Date(a.checkin.checkinTimestamp * 1000).toISOString();
          const dateB =
            b.calendarItem?.startDatetime ||
            new Date(b.checkin.checkinTimestamp * 1000).toISOString();
          return dateA.localeCompare(dateB);
        },
        cell: (row: AttendanceReportFragment) => (
          <div>
            {row.calendarItem ? (
              <>
                <Text variant="body-lg" color="wolf-800">
                  {formatRange(
                    new Date(row.calendarItem.startDatetime),
                    new Date(row.calendarItem.endDatetime),
                    "UTC",
                  )}
                </Text>
                <Text component="div" variant="body-lg" color="wolf-600">
                  {formatTimeRange(
                    new Date(row.calendarItem.startDatetime),
                    new Date(row.calendarItem.endDatetime),
                    "UTC",
                  )}
                </Text>
              </>
            ) : (
              <>
                <Text variant="body-lg" color="wolf-800">
                  {formatInTimeZone(
                    row.checkin.checkinTimestamp * 1000,
                    "UTC",
                    "EEEE, LLL d, yyyy",
                  )}
                </Text>
                <Text component="div" variant="body-lg" color="wolf-600">
                  {formatInTimeZone(
                    row.checkin.checkinTimestamp * 1000,
                    "UTC",
                    "h:mmaaa",
                  )}
                </Text>
              </>
            )}
          </div>
        ),
      },
      {
        header: "Plan",
        key: "plan",
        isSortable: true,
        showUp: "md",
        sort: (a: AttendanceReportFragment, b: AttendanceReportFragment) => {
          return a.subscription?.planObject?.name.localeCompare(
            b.subscription?.planObject?.name,
          );
        },
        cell: (row: AttendanceReportFragment) => (
          <Text variant="body-lg" color="wolf-800">
            {row.subscription?.planObject?.name || "--"}
          </Text>
        ),
      },
      {
        header: "Status",
        key: "status",
        isSortable: true,
        sort: (a: AttendanceReportFragment, b: AttendanceReportFragment) => {
          return a.status.localeCompare(b.status);
        },
        cell: (row: AttendanceReportFragment) => (
          <>
            {row.status === "WAIT_LIST" ? (
              <Grid container alignItems="center">
                <Grid item>
                  <AttendanceStatus status={row.status} size="small" />
                </Grid>
                <Grid item>
                  <IconButton
                    type="button"
                    icon={Icons.Clock}
                    color="#60717D"
                    tooltip={formatInTimeZone(
                      new Date(row.registration.waitlistUntilTimestamp * 1000),
                      client.timezone,
                      "'Expires' MMM dd, yyyy - h:mmaaa",
                    )}
                  />
                </Grid>
              </Grid>
            ) : (
              <AttendanceStatus status={row.status} size="small" />
            )}
          </>
        ),
      },
    ],
    [],
  );

  const {
    data,
    sort,
    filter,
    viewSaved,
    page,
    pageCount,
    filterTagOptions,
    viewLoading,
    handleSort,
    handleFilterChange,
    handlePageChange,
    handleViewSave,
    handleFilterTagDelete,
  } = useClientTable({
    data: attendanceReport?.getAttendanceReport || [],
    columns,
    defaultSort: { key: "date", order: "desc" },
    defaultRowsPerPage: 20,
    timezone: client.timezone,
    filterData: (data, dateRange, filter) => {
      return filterAttendanceReport(data, filter);
    },
    getFilterTagsMap: getAttendanceReportFilterTagsMap,
    getRowId: (row: AttendanceReportFragment) => {
      return row.registration?.uuid || row.checkin?.uuid;
    },
  });

  const renderFilter = useCallback(
    (
      filter: AttendanceFilterValue,
      handleFilterChange: (filter: AttendanceFilterValue) => void,
      handleClear: VoidFunction,
    ) => {
      return (
        <AttendanceFilter
          filter={filter}
          subscriptions={subscriptions || []}
          onFilterChange={handleFilterChange}
          onClear={handleClear}
        />
      );
    },
    [subscriptions],
  );

  const renderRowMenu = useCallback(
    (row: AttendanceReportFragment, closeMenu: VoidFunction) => {
      if (row?.status === "CANCELLED") {
        return null;
      }
      return (
        <AttendanceMenu row={row} onClose={closeMenu} onSuccess={refetch} />
      );
    },
    [refetch],
  );

  const getRowId = useCallback((row: AttendanceReportFragment) => {
    return row.registration?.uuid || row.checkin?.uuid;
  }, []);

  const { handleDownload } = useDownload({
    fileName: `attendance-${userUuid}.csv`,
    async getFileContent() {
      const filteredData = filterAttendanceReport(
        attendanceReport?.getAttendanceReport || [],
        filter,
      );
      return createAttendanceReportCsv(
        filteredData,
        profile?.getProfile,
        "UTC",
      );
    },
    onError(err) {
      toast.error(extractError(err));
    },
  });

  // applies a filter from the subscriptionUuid query param
  useLayoutEffect(() => {
    if (subscriptionStarted) {
      return;
    }
    if (subscriptions && subscriptionUuid) {
      subscriptionStartedActions.on();
      const selected = subscriptions.find(
        (item) => item.subscriptionUuid === subscriptionUuid,
      );
      if (selected) {
        handleFilterChange({
          ...filter,
          subscriptions: [selected],
        });
      }
    }
  }, [
    subscriptionStarted,
    subscriptionStartedActions,
    subscriptions,
    subscriptionUuid,
    handleFilterChange,
    filter,
  ]);

  if (!profile || !subscriptions) {
    return null;
  }

  return (
    <div>
      <AttendanceHeader
        profile={profile?.getProfile!}
        onDownloadClick={handleDownload}
      />
      <EnhancedTable
        columns={columns}
        data={data}
        dateRange={dateRange}
        filter={filter}
        filterTagOptions={filterTagOptions}
        loading={loading}
        page={page}
        pageCount={pageCount}
        sort={sort}
        viewLoading={viewLoading}
        viewSaved={viewSaved}
        hideSaveButton
        getRowId={getRowId}
        renderFilter={renderFilter}
        renderRowMenu={renderRowMenu}
        onDateRangeChange={setDateRange}
        onFilterChange={handleFilterChange}
        onFilterTagDelete={handleFilterTagDelete}
        onPageChange={handlePageChange}
        onSort={handleSort}
        onViewSave={handleViewSave}
      />
    </div>
  );
};

export default AttendanceReport;
